/**
 * Экран задач (docs/08-screens.md).
 *
 * Четыре вида переключаются табами, фильтры общие для всех и запоминаются
 * между сессиями. Кнопок создания и редактирования нет: Notion остаётся
 * источником правды, правки идут по ссылке.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Card, Checkbox, Icon, Select, Tabs } from '@/ds';
import { EmptyState } from '@/components/EmptyState';
import { Provenance } from '@/components/money';
import { ApiError } from '@/api/client';
import {
  useOperation,
  useTasks,
  type NotionSyncResult,
  type TaskFilterState,
} from '@/api/hooks';
import { formatRelative, plural } from '@/lib/format';
import { useControlSize } from '@/lib/useControlSize';
import { TaskBoard, TaskByPeople, TaskTable, TaskTimeline } from './tasks/TaskViews';
import { TaskAttention } from './tasks/TaskAttention';
import { QueryState } from '@/components/QueryState';

const STORAGE_KEY = 'kj.tasks.view';

const DEFAULT_FILTERS: TaskFilterState = {
  assignee: '',
  status: '',
  projectId: '',
  due: '',
  includeDone: true,
};

interface Stored {
  view: string;
  filters: TaskFilterState;
}

function readStored(): Stored {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { view: 'table', filters: DEFAULT_FILTERS };
    const parsed = JSON.parse(raw) as Partial<Stored>;
    return {
      view: parsed.view ?? 'table',
      filters: { ...DEFAULT_FILTERS, ...(parsed.filters ?? {}) },
    };
  } catch {
    return { view: 'table', filters: DEFAULT_FILTERS };
  }
}

const VIEWS = [
  { value: 'table', label: 'Таблица' },
  { value: 'board', label: 'Доска' },
  { value: 'people', label: 'По людям' },
  { value: 'timeline', label: 'Таймлайн' },
  { value: 'attention', label: 'Требуют внимания' },
];

const DUE_OPTIONS = [
  { value: '', label: 'Любой срок' },
  { value: 'overdue', label: 'Просроченные' },
  { value: 'this_week', label: 'Эта неделя' },
  { value: 'this_month', label: 'Этот месяц' },
  { value: 'no_date', label: 'Без срока' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Любой статус' },
  { value: 'not_started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
];

export function TasksScreen() {
  const size = useControlSize();
  const initial = readStored();
  // Ссылка из бота ведёт сразу на нужный вид.
  const [params] = useSearchParams();
  const requested = params.get('view');
  const [view, setView] = useState(
    requested && VIEWS.some((item) => item.value === requested) ? requested : initial.view,
  );
  const [filters, setFilters] = useState<TaskFilterState>(initial.filters);
  const [message, setMessage] = useState<string | null>(null);

  const tasks = useTasks(filters);
  const sync = useOperation<{ full?: boolean }, NotionSyncResult>('notion.sync');

  // Выбранный вид и фильтры запоминаются между сессиями.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ view, filters }));
    } catch {
      // Приватный режим браузера: настройки просто не запомнятся.
    }
  }, [view, filters]);

  const data = tasks.data;
  const set = (patch: Partial<TaskFilterState>) => setFilters((prev) => ({ ...prev, ...patch }));

  function refresh(full: boolean) {
    setMessage(null);
    sync.mutate(
      { full },
      {
        onSuccess: (result) => {
          setMessage(
            result.error
              ? `Не удалось обновить: ${result.error}`
              : `Получено ${result.fetched}, новых ${result.created}, обновлено ${result.updated}` +
                (result.archived > 0 ? `, архивировано ${result.archived}` : ''),
          );
        },
        onError: (error) => {
          setMessage(error instanceof ApiError ? error.message : 'Не удалось обновить задачи');
        },
      },
    );
  }

  if (tasks.isLoading || tasks.isError) {
    return (
      <div className="kj-page">
        <QueryState query={tasks} what="задачи" />
      </div>
    );
  }

  const failed = data?.sync.status === 'failed';

  return (
    <div className="kj-page">
      {/* Отчёт «Требуют внимания» фильтрам не подчиняется, поэтому их не показываем. */}
      {view === 'attention' ? null : (
      <Card padding="sm">
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 'var(--space-4)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 180, flex: '1 1 180px' }}>
            <Select
              size={size}
              label="Ответственный"
              options={[
                { value: '', label: 'Любой' },
                { value: 'unassigned', label: 'Не назначено' },
                ...(data?.people ?? []).map((person) => ({
                  value: person.notionUserId,
                  label: person.name,
                })),
              ]}
              value={filters.assignee}
              onChange={(e) => set({ assignee: e.target.value })}
            />
          </div>
          <div style={{ minWidth: 160, flex: '1 1 160px' }}>
            <Select
              size={size}
              label="Статус"
              options={STATUS_OPTIONS}
              value={filters.status}
              onChange={(e) => set({ status: e.target.value })}
            />
          </div>
          <div style={{ minWidth: 160, flex: '1 1 160px' }}>
            <Select
              size={size}
              label="Продукт"
              options={[
                { value: '', label: 'Любой' },
                ...(data?.projects ?? []).map((project) => ({
                  value: project.id,
                  label: project.name,
                })),
              ]}
              value={filters.projectId}
              onChange={(e) => set({ projectId: e.target.value })}
            />
          </div>
          <div style={{ minWidth: 160, flex: '1 1 160px' }}>
            <Select
              size={size}
              label="Срок"
              options={DUE_OPTIONS}
              value={filters.due}
              onChange={(e) => set({ due: e.target.value })}
            />
          </div>
          <div style={{ paddingBottom: 'var(--space-3)' }}>
            <Checkbox
              label="Показывать выполненные"
              checked={filters.includeDone}
              onChange={(e) => set({ includeDone: e.target.checked })}
            />
          </div>
        </div>
      </Card>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <div className="kj-tabs-touch kj-scroll-x">
          <Tabs items={VIEWS} value={view} onChange={setView} style={{ minWidth: 'max-content' }} />
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <Button
            variant="secondary"
            size="sm"
            loading={sync.isPending}
            onClick={() => refresh(false)}
            iconLeft={<Icon name="RefreshCw" size={16} />}
          >
            Обновить
          </Button>
          <Button variant="ghost" size="sm" onClick={() => refresh(true)} disabled={sync.isPending}>
            Полная сверка
          </Button>
        </div>
      </div>

      <div>
        {failed ? (
          <span
            style={{
              font: '400 13px/1.5 var(--font-text)',
              color: 'var(--status-warning-fg)',
            }}
          >
            {`Последняя синхронизация не удалась: ${data?.sync.error ?? ''}. Задачи показаны на ` +
              (data?.sync.lastSuccessAt
                ? formatRelative(data.sync.lastSuccessAt)
                : 'момент последнего успеха')}
          </span>
        ) : (
          <Provenance>
            {data?.sync.lastSuccessAt
              ? `Обновлено ${formatRelative(data.sync.lastSuccessAt)}. Правки делаются в Notion`
              : 'Задачи ещё не синхронизировались'}
          </Provenance>
        )}
        {message ? (
          <div
            style={{
              marginTop: 'var(--space-2)',
              font: '400 13px var(--font-text)',
              color: 'var(--text-muted)',
            }}
          >
            {message}
          </div>
        ) : null}
      </div>

      {view === 'attention' ? (
        <TaskAttention />
      ) : !data || data.tasks.length === 0 ? (
        <EmptyState
          icon="CheckSquare"
          title={data?.sync.lastSuccessAt ? 'С такими фильтрами задач нет' : 'Задачи ещё не загружены'}
          description={
            data?.sync.lastSuccessAt
              ? 'Снимите часть фильтров или загляните в Notion: возможно, задач с такими условиями действительно нет.'
              : 'Нажмите «Обновить», чтобы забрать задачи из базы «Все глобальные задачи команды». Приложение только читает их и никогда не пишет в Notion.'
          }
          action={
            <Button onClick={() => refresh(true)} loading={sync.isPending}>
              Обновить задачи
            </Button>
          }
        />
      ) : view === 'table' ? (
        <TaskTable tasks={data.tasks} />
      ) : view === 'board' ? (
        <TaskBoard tasks={data.tasks} />
      ) : view === 'people' ? (
        <TaskByPeople data={data} />
      ) : (
        <TaskTimeline data={data} />
      )}

      {view !== 'attention' && data && data.tasks.length > 0 ? (
        <Provenance>
          {`${data.tasks.length} ${plural(data.tasks.length, 'задача', 'задачи', 'задач')} с учётом фильтров`}
        </Provenance>
      ) : null}
    </div>
  );
}
